import type { SlotTheme, PaylineDefinition } from '../types';

// =============================================================================
// Payline Definitions (20 standard video slot paylines)
// =============================================================================

const PAYLINES: PaylineDefinition[] = [
  // Horizontal lines
  [1, 1, 1, 1, 1], // Line 1: Middle row
  [0, 0, 0, 0, 0], // Line 2: Top row
  [2, 2, 2, 2, 2], // Line 3: Bottom row

  // V shapes
  [0, 1, 2, 1, 0], // Line 4: V shape
  [2, 1, 0, 1, 2], // Line 5: Inverted V (mountain)

  // Zigzags
  [0, 0, 1, 2, 2], // Line 6: Descending zigzag
  [2, 2, 1, 0, 0], // Line 7: Ascending zigzag
  [1, 0, 0, 0, 1], // Line 8: U shape top
  [1, 2, 2, 2, 1], // Line 9: U shape bottom

  // Steps
  [0, 1, 1, 1, 0], // Line 10: Shallow V
  [2, 1, 1, 1, 2], // Line 11: Shallow mountain
  [0, 1, 0, 1, 0], // Line 12: Alternating top
  [2, 1, 2, 1, 2], // Line 13: Alternating bottom

  // Complex patterns
  [1, 0, 1, 2, 1], // Line 14: Wave down
  [1, 2, 1, 0, 1], // Line 15: Wave up
  [0, 0, 2, 0, 0], // Line 16: Dip
  [2, 2, 0, 2, 2], // Line 17: Peak
  [0, 2, 0, 2, 0], // Line 18: High alternating
  [2, 0, 2, 0, 2], // Line 19: Low alternating
  [1, 0, 2, 0, 1], // Line 20: Diamond
];

// =============================================================================
// Reel Strip (defines symbol distribution/probability)
// More occurrences = higher probability of landing
// NOTE: RUMIO letters are NOT in this strip - they're handled specially by backend
// Each letter only appears in its designated column (R=col1, U=col2, etc.)
// =============================================================================

const REEL_STRIP = [
  // Low-Medium value (furniture) - most common
  'plant',
  'plant',
  'plant',
  'plant',
  'table',
  'table',
  'table',
  'desk',
  'desk',
  'desk',

  // Medium value
  'chair',
  'chair',
  'chair',
  'bookcase',
  'bookcase',
  'money_bag',
  'money_bag',

  // High value
  'lamp',
  'lamp',
  'sofa',

  // Special symbols (rare)
  'wild',
  'scatter',
  'bonus',
  'rumio_ring', // Wild + highest value
];

// Column-specific reel strips for RUMIO letters
// Backend uses these to inject letters with ~2-3% probability
export const COLUMN_LETTERS: Record<number, string> = {
  0: 'letter_r', // Column 1
  1: 'letter_u', // Column 2
  2: 'letter_m', // Column 3
  3: 'letter_i', // Column 4
  4: 'letter_o', // Column 5
};

// =============================================================================
// Theme Configuration
// =============================================================================

export const rumioDefaultTheme: SlotTheme = {
  id: 'rumio-default',
  name: 'Rumio Casino',
  version: '1.0.0',
  description: 'The official Rumio slot machine theme featuring furniture and the iconic gold ring.',
  author: 'Rumio Team',

  // ---------------------------------------------------------------------------
  // Grid Configuration
  // ---------------------------------------------------------------------------
  grid: {
    rows: 3,
    columns: 5,
    paylines: 20,
  },

  paylines: PAYLINES,
  reelStrip: REEL_STRIP,

  // ---------------------------------------------------------------------------
  // Symbol Definitions
  // ---------------------------------------------------------------------------
  symbols: {
    // === WILD SYMBOLS ===
    rumio_ring: {
      id: 'rumio_ring',
      name: 'Rumio Gold Ring',
      type: 'wild',
      model: '/themes/rumio/symbols/rumio-ring.glb',
      sprite: '/themes/rumio/symbols/rumio-ring.png',
      paytable: { 3: 50, 4: 200, 5: 1000 },
      isWild: true,
      canExpand: true,
      expandChance: 0.05, // 5% chance to expand
      glowColor: '#FFD700',
      modelScale: 1.2,
    },

    wild: {
      id: 'wild',
      name: 'Wild',
      type: 'wild',
      renderMode: 'text3d',
      textContent: 'WILD',
      model: '/themes/rumio/symbols/wild.glb', // Fallback if text3d fails
      sprite: '/themes/rumio/symbols/wild.png',
      isWild: true,
      canExpand: true,
      expandChance: 0.03, // 3% chance to expand
      glowColor: '#FFD700', // Gold for maximum visibility
    },

    // === SCATTER SYMBOL ===
    scatter: {
      id: 'scatter',
      name: 'Scatter Star',
      type: 'scatter',
      model: '/themes/rumio/symbols/scatter.glb',
      sprite: '/themes/rumio/symbols/scatter.png',
      paytable: { 3: 2, 4: 10, 5: 50 }, // Pays total bet multiplier
      paysAnywhere: true,
      triggersFeature: 'free_spins',
      minForTrigger: 3,
      glowColor: '#9333EA', // Purple
      modelScale: 1.0,
    },

    // === BONUS SYMBOL ===
    bonus: {
      id: 'bonus',
      name: 'Mystery Box',
      type: 'bonus',
      model: '/themes/rumio/symbols/bonus.glb',
      sprite: '/themes/rumio/symbols/bonus.png',
      triggersFeature: 'bonus_game',
      minForTrigger: 3,
      triggerOnReels: [0, 1, 2], // Only triggers on reels 1-3 (0-indexed)
      glowColor: '#10B981', // Green
      modelScale: 1.0,
    },

    // === HIGH VALUE (Furniture) ===
    sofa: {
      id: 'sofa',
      name: 'Designer Sofa',
      type: 'high_value',
      model: '/themes/rumio/symbols/sofa.glb',
      sprite: '/themes/rumio/symbols/sofa.png',
      paytable: { 3: 30, 4: 100, 5: 500 },
      glowColor: '#3B82F6', // Blue
      modelScale: 1.15, // Wide and low - scale up slightly
    },

    lamp: {
      id: 'lamp',
      name: 'Modern Lamp',
      type: 'high_value',
      model: '/themes/rumio/symbols/lamp.glb',
      sprite: '/themes/rumio/symbols/lamp.png',
      paytable: { 3: 25, 4: 80, 5: 400 },
      glowColor: '#F59E0B', // Amber
      modelScale: 1.1, // Tall and thin - scale up slightly
    },

    money_bag: {
      id: 'money_bag',
      name: 'Money Bag',
      type: 'high_value',
      model: '/themes/rumio/symbols/money-bag.glb',
      sprite: '/themes/rumio/symbols/money-bag.png',
      paytable: { 3: 20, 4: 70, 5: 350 },
      glowColor: '#FBBF24', // Gold/Amber
      modelScale: 1.2, // Compact - scale up for visual weight
    },

    // === MEDIUM VALUE (Furniture) ===
    bookcase: {
      id: 'bookcase',
      name: 'Bookcase',
      type: 'medium_value',
      model: '/themes/rumio/symbols/bookcase.glb',
      sprite: '/themes/rumio/symbols/bookcase.png',
      paytable: { 3: 18, 4: 60, 5: 300 },
      glowColor: '#A16207', // Amber/Brown
      modelScale: 0.95, // Tall - scale down slightly to fit
    },

    chair: {
      id: 'chair',
      name: 'Accent Chair',
      type: 'medium_value',
      model: '/themes/rumio/symbols/chair.glb',
      sprite: '/themes/rumio/symbols/chair.png',
      paytable: { 3: 20, 4: 60, 5: 300 },
      glowColor: '#EC4899', // Pink
      modelScale: 1.1, // Balanced - slight scale up
    },

    table: {
      id: 'table',
      name: 'Coffee Table',
      type: 'medium_value',
      model: '/themes/rumio/symbols/table.glb',
      sprite: '/themes/rumio/symbols/table.png',
      paytable: { 3: 15, 4: 50, 5: 250 },
      glowColor: '#8B5CF6', // Purple
      modelScale: 1.15, // Wide and low - scale up
    },

    desk: {
      id: 'desk',
      name: 'Modern Desk',
      type: 'medium_value',
      model: '/themes/rumio/symbols/desk.glb',
      sprite: '/themes/rumio/symbols/desk.png',
      paytable: { 3: 12, 4: 45, 5: 225 },
      glowColor: '#78716C', // Stone gray
      modelScale: 1.0, // Wide - keep at base scale
    },

    plant: {
      id: 'plant',
      name: 'Indoor Plant',
      type: 'medium_value',
      model: '/themes/rumio/symbols/plant.glb',
      modelChildName: 'Pot_13_Low_PolyPlant_0',
      sprite: '/themes/rumio/symbols/plant.png',
      paytable: { 3: 10, 4: 40, 5: 200 },
      glowColor: '#22C55E', // Green
      modelScale: 1.25, // Small - scale up for visual presence
    },

    // === JACKPOT SYMBOLS (Letters R-U-M-I-O) ===
    // Each letter ONLY appears in its designated column for the RUMIO Jackpot
    // Getting all 5 letters = JACKPOT! (50x bet + Pro+ subscription)
    letter_r: {
      id: 'letter_r',
      name: 'Letter R',
      type: 'low_value', // Marked low_value but forms JACKPOT when all 5 appear
      renderMode: 'text3d',
      textContent: 'R',
      model: '/themes/rumio/symbols/letter-r.glb',
      sprite: '/themes/rumio/symbols/letter-r.png',
      // No individual paytable - only pays as part of RUMIO jackpot
      glowColor: '#EF4444', // Red
    },

    letter_u: {
      id: 'letter_u',
      name: 'Letter U',
      type: 'low_value',
      renderMode: 'text3d',
      textContent: 'U',
      model: '/themes/rumio/symbols/letter-u.glb',
      sprite: '/themes/rumio/symbols/letter-u.png',
      glowColor: '#F97316', // Orange
    },

    letter_m: {
      id: 'letter_m',
      name: 'Letter M',
      type: 'low_value',
      renderMode: 'text3d',
      textContent: 'M',
      model: '/themes/rumio/symbols/letter-m.glb',
      sprite: '/themes/rumio/symbols/letter-m.png',
      glowColor: '#EAB308', // Yellow
    },

    letter_i: {
      id: 'letter_i',
      name: 'Letter I',
      type: 'low_value',
      renderMode: 'text3d',
      textContent: 'I',
      model: '/themes/rumio/symbols/letter-i.glb',
      sprite: '/themes/rumio/symbols/letter-i.png',
      glowColor: '#84CC16', // Lime
    },

    letter_o: {
      id: 'letter_o',
      name: 'Letter O',
      type: 'low_value',
      renderMode: 'text3d',
      textContent: 'O',
      model: '/themes/rumio/symbols/letter-o.glb',
      sprite: '/themes/rumio/symbols/letter-o.png',
      glowColor: '#06B6D4', // Cyan
    },
  },

  // ---------------------------------------------------------------------------
  // Visual Style
  // ---------------------------------------------------------------------------
  style: {
    primary: '#FFD700', // Gold
    secondary: '#1E3A5F', // Deep blue
    accent: '#FF6B35', // Rumio orange
    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)',
    winGlow: '#FFD700',
    reelBackground: '#0A0E17',
    textPrimary: '#FFFFFF',
    textSecondary: '#94A3B8',
    winText: '#FFD700',
    fontFamily: "'Inter', sans-serif",
    fontFamilyNumbers: "'JetBrains Mono', monospace",
  },

  // ---------------------------------------------------------------------------
  // Cabinet Configuration
  // ---------------------------------------------------------------------------
  cabinet: {
    model: '/themes/rumio/cabinet/cabinet.glb',
    buttonModel: '/themes/rumio/cabinet/spin-button.glb',
    material: {
      color: '#1E3A5F',
      metalness: 0.8,
      roughness: 0.2,
      envMapIntensity: 1.5,
    },
    lights: [
      // Main ambient
      { type: 'ambient', color: '#FFFFFF', intensity: 0.4 },
      // Key light (front)
      {
        type: 'directional',
        position: [5, 10, 5],
        color: '#FFFFFF',
        intensity: 1.0,
      },
      // Fill light (side)
      {
        type: 'point',
        position: [-5, 5, 3],
        color: '#FFD700',
        intensity: 0.5,
      },
      // Rim light (back)
      {
        type: 'point',
        position: [0, 3, -5],
        color: '#FF6B35',
        intensity: 0.3,
      },
      // Win highlight spots
      {
        type: 'spot',
        position: [0, 8, 2],
        color: '#FFD700',
        intensity: 0,
        angle: 0.5,
        penumbra: 0.5,
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Animation Configuration
  // ---------------------------------------------------------------------------
  animations: {
    spinStyle: 'classic',
    spinDuration: 2500,
    reelStopDelay: 200,
    reelEasing: 'easeOutBack',
    winCelebration: 'confetti',
    bigWinThreshold: 50, // 50x bet
    megaWinThreshold: 200, // 200x bet
    winLineHighlightDuration: 2000,
    particleCount: 100,
    reelBounceIntensity: 0.15,
    anticipation: true,
    anticipationDuration: 150,
  },

  // ---------------------------------------------------------------------------
  // Audio Configuration
  // ---------------------------------------------------------------------------
  audio: {
    spinStart: '/themes/rumio/audio/spin-start.mp3',
    reelStop: '/themes/rumio/audio/reel-stop.mp3',
    reelTick: '/themes/rumio/audio/reel-tick.mp3',
    smallWin: '/themes/rumio/audio/win-small.mp3',
    mediumWin: '/themes/rumio/audio/win-medium.mp3',
    bigWin: '/themes/rumio/audio/win-big.mp3',
    jackpot: '/themes/rumio/audio/jackpot.mp3',
    freeSpinsTrigger: '/themes/rumio/audio/free-spins.mp3',
    bonusTrigger: '/themes/rumio/audio/bonus.mp3',
    wildExpand: '/themes/rumio/audio/wild-expand.mp3',
    ambient: '/themes/rumio/audio/ambient.mp3',
    freeSpinsMusic: '/themes/rumio/audio/free-spins-music.mp3',
    bonusMusic: '/themes/rumio/audio/bonus-music.mp3',
    buttonClick: '/themes/rumio/audio/click.mp3',
    coinSound: '/themes/rumio/audio/coin.mp3',
  },

  // ---------------------------------------------------------------------------
  // UI Text (English)
  // ---------------------------------------------------------------------------
  text: {
    spinButton: 'SPIN',
    autoSpin: 'AUTO',
    stop: 'STOP',
    freeSpinsTitle: 'FREE SPINS!',
    freeSpinsRemaining: 'Spins Remaining',
    bonusGameTitle: 'BONUS ROUND!',
    win: 'WIN',
    bigWin: 'BIG WIN!',
    megaWin: 'MEGA WIN!',
    jackpot: 'JACKPOT!!!',
    bet: 'BET',
    lines: 'LINES',
    balance: 'BALANCE',
    totalBet: 'TOTAL BET',
    paytable: 'PAYTABLE',
    info: 'INFO',
  },

  // ---------------------------------------------------------------------------
  // Free Spins Configuration
  // ---------------------------------------------------------------------------
  freeSpins: {
    awards: {
      3: { spins: 10, multiplier: 2 },
      4: { spins: 15, multiplier: 3 },
      5: { spins: 25, multiplier: 5 },
    },
    canRetrigger: true,
    maxRetriggerSpins: 50,
    background: 'linear-gradient(135deg, #4C1D95 0%, #7C3AED 50%, #4C1D95 100%)',
  },

  // ---------------------------------------------------------------------------
  // Bonus Game Configuration
  // ---------------------------------------------------------------------------
  bonusGame: {
    itemCount: 12,
    picksAllowed: 3,
    itemStyle: 'box',
    itemModel: '/themes/rumio/bonus/mystery-box.glb',
    background: 'linear-gradient(135deg, #065F46 0%, #10B981 50%, #065F46 100%)',
  },

  // ---------------------------------------------------------------------------
  // RUMIO Jackpot Configuration
  // Triggered when R-U-M-I-O letters appear across all 5 columns on any row
  // Pro+ duration scales with bet per line
  // ---------------------------------------------------------------------------
  rumioJackpot: {
    // Payout multiplier (50x total bet)
    payoutMultiplier: 50,
    // Letter appearance probability per column (~2.5% chance)
    letterProbability: 0.025,
    // Pro+ subscription duration based on bet per line
    subscriptionTiers: [
      { minBet: 1, maxBet: 1, months: 1, name: '1 Month Pro+' },
      { minBet: 2, maxBet: 3, months: 2, name: '2 Months Pro+' },
      { minBet: 4, maxBet: 5, months: 3, name: '3 Months Pro+' },
      { minBet: 6, maxBet: 7, months: 6, name: '6 Months Pro+' },
      { minBet: 8, maxBet: 10, months: 12, name: '1 Year Pro+' },
    ],
    // Celebration settings
    celebration: 'fireworks',
    icon: '/themes/rumio/prizes/rumio-jackpot.png',
  },

  // Legacy jackpot prizes (kept for backwards compatibility)
  jackpotPrizes: [],

  // ---------------------------------------------------------------------------
  // Adaptive Rendering
  // ---------------------------------------------------------------------------
  adaptive: {
    enabled: true,
    highThreshold: 70,
    mediumThreshold: 40,
    qualitySettings: {
      high: {
        shadows: true,
        particles: true,
        postProcessing: true,
        antialiasing: true,
        modelDetail: 'high',
      },
      medium: {
        shadows: false,
        particles: true,
        postProcessing: false,
        antialiasing: true,
        modelDetail: 'high',
      },
      low: {
        shadows: false,
        particles: false,
        postProcessing: false,
        antialiasing: false,
        modelDetail: 'low',
      },
      '2d': {
        shadows: false,
        particles: false,
        postProcessing: false,
        antialiasing: false,
        modelDetail: 'low',
      },
    },
  },

  // ---------------------------------------------------------------------------
  // Betting Configuration
  // ---------------------------------------------------------------------------
  minLines: 1,
  maxLines: 20,
  betOptions: [1, 2, 3, 5, 10],
  defaultBet: 1,
  defaultLines: 20,
};

export default rumioDefaultTheme;
